import { Fragment, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, titleCase } from '../../lib/format'
import type { ComplianceNotice, ListResponse } from '../../types'
import { NOTICE_STATUSES, NOTICE_TYPES } from './types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton, StatusBadge, Textarea,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, DocIcon, PlusIcon } from '../../components/icons'

type Notice = ComplianceNotice & {
  org_id?: string
  notice_type?: string
  resolution_deadline?: string
  site_id?: string
}

/* Values must be real compliance_service.NOTICE_STATUSES entries (see the
 * comment on that const in ./types) - this used to offer 'open'/'in_progress',
 * neither of which any notice can ever actually have, so the default filter
 * always showed "nothing outstanding" even with real open notices. */
const FILTERS = [
  { value: 'issued', label: 'Issued' },
  { value: '', label: 'All' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'escalated', label: 'Escalated' },
]

export default function GovNotices() {
  const qc = useQueryClient()
  const [status, setStatus] = useState('issued')
  const [showForm, setShowForm] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'notices', status],
    queryFn: () => api.get<ListResponse<Notice>>(
      `/gov/notices${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  })

  const issue = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/gov/notices', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gov'] }); setShowForm(false) },
  })

  const setStatusMut = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) =>
      api.post(`/gov/notices/${id}/status`, { status: next, notes: note }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov'] })
      setOpenId(null)
      setNote('')
    },
  })

  // Escalation isn't just another status - it creates a new, higher-severity
  // notice (warning -> improvement -> suspension -> penalty) and marks this
  // one escalated, so it gets its own action rather than being one of the
  // plain NOTICE_STATUSES buttons.
  const escalate = useMutation({
    mutationFn: (id: string) => api.post(`/gov/notices/${id}/escalate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov'] })
      setOpenId(null)
      setNote('')
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    issue.mutate({
      org_id: f.get('org_id'),
      notice_type: f.get('notice_type'),
      description: f.get('description'),
      site_id: f.get('site_id') || '',
      resolution_deadline: f.get('resolution_deadline') || '',
    })
  }

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Compliance notices</h1>
          <p className="page-head__sub">Issue and track notices against cooperatives.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Issue notice'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Issue notice'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Issue a compliance notice"
                      sub="The cooperative sees this immediately in their compliance view." />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {issue.isError && <Alert tone="danger">{(issue.error as Error).message}</Alert>}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Who & what kind', content: (
                      <div className="grid grid--2">
                        <Input label="Cooperative ID" name="org_id" required
                               placeholder="Organisation this applies to" />
                        <Select label="Notice type" name="notice_type" required defaultValue="warning">
                          {NOTICE_TYPES.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
                        </Select>
                      </div>
                    ) },
                    { title: 'Scope & deadline', content: (
                      <div className="grid grid--2">
                        <Input label="Site ID (optional)" name="site_id"
                               placeholder="Leave blank if org-wide" />
                        <Input label="Resolution deadline" name="resolution_deadline" type="date" />
                      </div>
                    ) },
                    { title: 'Description', content: (
                      <Textarea label="Description" name="description" required
                                placeholder="What is non-compliant, and what must change." />
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={issue.isPending}>Issue notice</Button>
                      <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        {FILTERS.map((f) => (
          <Button key={f.label} size="sm"
                  variant={status === f.value ? 'primary' : 'secondary'}
                  onClick={() => setStatus(f.value)}>
            {f.label}
          </Button>
        ))}
      </div>

      {setStatusMut.isError && <Alert tone="danger">{(setStatusMut.error as Error).message}</Alert>}
      {escalate.isError && <Alert tone="danger">{(escalate.error as Error).message}</Alert>}

      <Card>
        <CardHeader title="Notices"
                    sub={items.length ? `${items.length} notice${items.length === 1 ? '' : 's'}` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="70%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={<DocIcon size={22} />} title="No notices"
                        body={status === 'issued' ? 'Nothing outstanding in your area.' : 'Try a different filter.'} />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Cooperative</th><th>Type</th><th>Description</th>
                      <th>Deadline</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {items.map((n, i) => {
                    const id = n.id ?? String(i)
                    return (
                      <Fragment key={id}>
                        <tr>
                          <td>{n.org_id || '—'}</td>
                          <td>{titleCase(n.notice_type)}</td>
                          <td style={{ maxWidth: 300 }}>{n.description || n.title || '—'}</td>
                          <td>{formatDate(n.resolution_deadline)}</td>
                          <td><StatusBadge status={n.status || 'issued'} /></td>
                          <td className="table__num">
                            {n.id && (
                              <Button size="sm" variant="secondary"
                                      onClick={() => { setOpenId(openId === id ? null : id); setNote('') }}>
                                {openId === id ? 'Close' : 'Update'}
                              </Button>
                            )}
                          </td>
                        </tr>
                        {openId === id && n.id && (
                          <tr>
                            <td colSpan={6} style={{ background: 'var(--bg-sunken)' }}>
                              <div className="stack" style={{ padding: 'var(--sp-2) 0' }}>
                                <Textarea label="Resolution notes"
                                          placeholder="Recorded against the notice."
                                          value={note}
                                          onChange={(e) => setNote(e.target.value)} />
                                <div className="row" style={{ flexWrap: 'wrap' }}>
                                  {/* "escalated" is set as a side effect of the dedicated Escalate
                                      action below (it creates a new, higher-severity notice) - it
                                      isn't a plain status flip, so it's excluded from this list. */}
                                  {NOTICE_STATUSES.filter((s) => s !== 'escalated').map((s) => (
                                    <Button key={s} size="sm"
                                            variant={s === 'resolved' ? 'primary' : 'secondary'}
                                            disabled={setStatusMut.isPending || s === (n.status || 'issued')}
                                            onClick={() => setStatusMut.mutate({ id: n.id!, next: s })}>
                                      {titleCase(s)}
                                    </Button>
                                  ))}
                                  <Button size="sm" variant="danger"
                                          loading={escalate.isPending}
                                          // Was disabled only once THIS notice's own status flips to
                                          // "escalated" - but a notice can be issued directly at
                                          // closure_order (the top of NOTICE_TYPES/NOTICE_ESCALATION)
                                          // and stay "issued" forever, since nothing else ever moves
                                          // its status. The button stayed clickable, and escalate_notice
                                          // has nowhere to escalate TO, so the backend 409s with a raw
                                          // "already_at_highest_severity" code that this component then
                                          // rendered verbatim via the isError Alert below - a real user
                                          // (a gov official) could click Escalate on a closure_order
                                          // notice and see that unformatted code with no other feedback.
                                          disabled={n.status === 'escalated' ||
                                            n.notice_type === NOTICE_TYPES[NOTICE_TYPES.length - 1]}
                                          onClick={() => n.id && escalate.mutate(n.id)}>
                                    Escalate
                                  </Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
